import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Spinner } from 'src/components/atoms';
import { AtlasMuiThemeProvider } from 'src/theme/atlas/atlasMuiThemeProvider';
import { InformationBanner } from 'src/components/molecules/InformationBanner/InformationBanner';
import { handleErrorToast } from 'src/components/molecules/Toast/Toast';
import { TransformedCategory } from 'src/components/pages/FormCreation/components/Configuration/helpers/categoryDataTransformers';
import { useTranslateFormDraftContentMutation } from 'src/graphql';
import { FormState } from 'src/types';
import { LanguageOption } from 'src/utils/languages';
import {
  CategorySelection,
  Configuration,
  FormTitleAndDescription,
  LanguageVariants,
  Questions,
} from './components';
import type { QuestionCardCopy } from './components/QuestionCard/QuestionCard';
import {
  CategoryWarningBannerWrapper,
  ConfigSidebar,
  GridContainer,
  GridMainColumn,
  MainContent,
  PageContent,
  FormTranslatingPaneOverlay,
  FormTranslationOverlayCopy,
  FormTranslationOverlayStack,
} from './FormCreation.styles';
import { buildFormDraftTranslateContentInput } from './helpers/buildFormDraftTranslateContentInput';
import { fingerprintFormTranslateSource } from './helpers/fingerprintFormTranslateSource';
import { localeHasFormTranslationRows } from './helpers/localeHasFormTranslationRows';
import {
  DEFAULT_FORM_EDITING_LOCALE,
  resolveDefaultEditingLocale,
} from './helpers/localizedQuestionFieldHelpers';
import { mergeFormTranslateDraftContentIntoQuestions } from './helpers/mergeFormTranslateDraftContentIntoQuestions';
import { seedFormAutoTranslateLastFingerprintFromHydratedQuestions } from './helpers/seedFormAutoTranslateLastFingerprintFromHydratedQuestions';
import { CountryOption, FormCreationMode, QuestionData } from './types';

const ENGLISH_QUESTION_CARD_COPY: QuestionCardCopy = {
  requiredSuffix: '(Required)',
};

export interface FormCreationQuestionAreaProps {
  mode: FormCreationMode;
  isSpeakUpForm: boolean;
  selectedLanguages: LanguageOption[];
  onAddDefaultLanguage: () => void;
  setSelectedLanguages: (languages: LanguageOption[]) => void;
  onCategorySelect: (category: TransformedCategory) => void;
  isEditMode: boolean;
  title: string;
  setTitle: (value: string) => void;
  description: string;
  setDescription: (value: string) => void;
  titleTranslations: Record<string, string>;
  setTitleTranslations: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >;
  descriptionTranslations: Record<string, string>;
  setDescriptionTranslations: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >;
  titleTouched: boolean;
  setTitleTouched: (value: boolean) => void;
  descriptionTouched: boolean;
  setDescriptionTouched: (value: boolean) => void;
  categoryName: string;
  setCategoryName: (value: string) => void;
  setCategoryId: (value: number | null) => void;
  categoryNameTouched: boolean;
  setCategoryNameTouched: (value: boolean) => void;
  categoryDescription: string;
  setCategoryDescription: (value: string) => void;
  categoryDescriptionTouched: boolean;
  setCategoryDescriptionTouched: (value: boolean) => void;
  selectedCountry: CountryOption[];
  setSelectedCountry: (value: CountryOption[]) => void;
  countryTouched: boolean;
  setCountryTouched: (value: boolean) => void;
  autoReviewEnabled: boolean;
  setAutoReviewEnabled: (value: boolean) => void;
  onSpeakUpCategoryChosenInConfig?: () => void;
  questions: QuestionData[];
  setQuestions: React.Dispatch<React.SetStateAction<QuestionData[]>>;
  questionRefs: React.MutableRefObject<Map<string, HTMLDivElement>>;
  isDuplicateManagementDraft?: boolean;
  translateFingerprintSeedKey?: string | null;
  setActiveEditingLocaleRef?: React.MutableRefObject<
    ((locale: string) => void) | null
  >;
}

export const FormCreationQuestionArea = ({
  mode,
  isSpeakUpForm,
  selectedLanguages,
  onAddDefaultLanguage,
  setSelectedLanguages,
  onCategorySelect,
  title,
  setTitle,
  description,
  setDescription,
  titleTranslations,
  setTitleTranslations,
  descriptionTranslations,
  setDescriptionTranslations,
  titleTouched,
  setTitleTouched,
  descriptionTouched,
  setDescriptionTouched,
  categoryName,
  setCategoryName,
  setCategoryId,
  categoryNameTouched,
  setCategoryNameTouched,
  categoryDescription,
  setCategoryDescription,
  categoryDescriptionTouched,
  setCategoryDescriptionTouched,
  selectedCountry,
  setSelectedCountry,
  countryTouched,
  setCountryTouched,
  autoReviewEnabled,
  setAutoReviewEnabled,
  onSpeakUpCategoryChosenInConfig,
  isEditMode,
  questions,
  setQuestions,
  questionRefs,
  isDuplicateManagementDraft = false,
  translateFingerprintSeedKey,
  setActiveEditingLocaleRef: externalLocaleRef,
}: FormCreationQuestionAreaProps) => {
  const [activeEditingLocale, setActiveEditingLocale] = useState('en-GB');
  const activeEditingLocaleRef = useRef(activeEditingLocale);
  activeEditingLocaleRef.current = activeEditingLocale;

  if (externalLocaleRef) {
    externalLocaleRef.current = setActiveEditingLocale;
  }

  const questionsRef = useRef(questions);
  questionsRef.current = questions;
  const categoryNameRef = useRef(categoryName);
  categoryNameRef.current = categoryName;
  const categoryDescriptionRef = useRef(categoryDescription);
  categoryDescriptionRef.current = categoryDescription;
  const titleRef = useRef(title);
  titleRef.current = title;
  const descriptionRef = useRef(description);
  descriptionRef.current = description;

  const { mutate: translateFormDraft, isLoading: isFormTranslating } =
    useTranslateFormDraftContentMutation();
  const { mutate: translateFormUiCopy } =
    useTranslateFormDraftContentMutation();
  const [questionCardCopyByLocale, setQuestionCardCopyByLocale] = useState<
    Record<string, QuestionCardCopy>
  >({ [DEFAULT_FORM_EDITING_LOCALE]: ENGLISH_QUESTION_CARD_COPY });
  const lastAutoTranslateSourceByLocaleRef = useRef<Record<string, string>>({});
  useEffect(() => {
    lastAutoTranslateSourceByLocaleRef.current = {};
  }, [translateFingerprintSeedKey]);

  useEffect(() => {
    if (mode !== 'content' && mode !== 'edit') return;
    if (questions.length === 0) return;
    seedFormAutoTranslateLastFingerprintFromHydratedQuestions(
      lastAutoTranslateSourceByLocaleRef.current,
      questions,
      title,
      description,
      selectedLanguages,
    );
  }, [mode, questions, title, description, selectedLanguages]);

  useEffect(() => {
    if (selectedLanguages.length === 0) return;
    if (!selectedLanguages.some((l) => l.value === activeEditingLocale)) {
      setActiveEditingLocale(resolveDefaultEditingLocale(selectedLanguages));
    }
  }, [selectedLanguages, activeEditingLocale]);

  useEffect(() => {
    if (mode !== 'content' && mode !== 'edit') {
      return;
    }
    if (activeEditingLocale === DEFAULT_FORM_EDITING_LOCALE) {
      return;
    }

    // Skip API call if translations already exist in state for all form types
    const hasTitleTranslation = titleTranslations[activeEditingLocale];
    const hasDescriptionTranslation =
      descriptionTranslations[activeEditingLocale];

    if (hasTitleTranslation || hasDescriptionTranslation) {
      // Translations already loaded from backend, skip API call
      return;
    }

    const snapshotQuestions = questionsRef.current;
    const fp = fingerprintFormTranslateSource(
      snapshotQuestions,
      titleRef.current,
      descriptionRef.current,
    );

    const hasRows = localeHasFormTranslationRows(
      snapshotQuestions,
      activeEditingLocale,
    );
    const lastFp =
      lastAutoTranslateSourceByLocaleRef.current[activeEditingLocale];

    if (hasRows && lastFp === fp) {
      return;
    }

    const isResyncFromEnglish =
      hasRows && lastFp !== undefined && lastFp !== fp;

    const localeAtStart = activeEditingLocale;
    const input = buildFormDraftTranslateContentInput({
      targetLanguageCode: localeAtStart,
      formTitle: titleRef.current,
      formDescription: descriptionRef.current,
      questions: snapshotQuestions,
    });

    translateFormDraft(
      { input },
      {
        onSuccess: (data) => {
          if (activeEditingLocaleRef.current !== localeAtStart) {
            return;
          }

          // Store title/description translations
          const payload = data.translateFormDraftContent;

          if (payload.formTitle) {
            setTitleTranslations((prev) => ({
              ...prev,
              [localeAtStart]: payload.formTitle!,
            }));
          }
          if (payload.formDescription) {
            setDescriptionTranslations((prev) => ({
              ...prev,
              [localeAtStart]: payload.formDescription!,
            }));
          }

          setQuestions((prev) => {
            if (
              !isResyncFromEnglish &&
              localeHasFormTranslationRows(prev, localeAtStart)
            ) {
              return prev;
            }
            lastAutoTranslateSourceByLocaleRef.current[localeAtStart] = fp;
            return mergeFormTranslateDraftContentIntoQuestions(
              prev,
              localeAtStart,
              data.translateFormDraftContent,
            );
          });
        },
        onError: () => {
          if (activeEditingLocaleRef.current !== localeAtStart) return;
          handleErrorToast({
            toastMessage: 'Translation failed',
            toastDescription:
              'Could not translate draft content for this language. You can still enter text manually.',
          });
        },
      },
    );
  }, [
    mode,
    activeEditingLocale,
    isSpeakUpForm,
    titleTranslations,
    descriptionTranslations,
    setQuestions,
    translateFormDraft,
    setTitleTranslations,
    setDescriptionTranslations,
  ]);

  const cachedLocaleUiCopy = questionCardCopyByLocale[activeEditingLocale];
  useEffect(() => {
    if (mode !== 'content' && mode !== 'edit') return;
    if (activeEditingLocale === DEFAULT_FORM_EDITING_LOCALE) return;
    if (cachedLocaleUiCopy) return;

    translateFormUiCopy(
      {
        input: {
          sourceLanguageCode: DEFAULT_FORM_EDITING_LOCALE,
          targetLanguageCode: activeEditingLocale,
          formTitle: null,
          formDescription: null,
          introduction: null,
          questions: [
            {
              orderIndex: 1,
              questionText: '',
              description: ENGLISH_QUESTION_CARD_COPY.requiredSuffix,
            },
          ],
        },
      },
      {
        onSuccess: (data) => {
          const payload = data.translateFormDraftContent;
          setQuestionCardCopyByLocale((prev) => ({
            ...prev,
            [activeEditingLocale]: {
              requiredSuffix:
                payload.questions?.[0]?.description?.trim() ||
                ENGLISH_QUESTION_CARD_COPY.requiredSuffix,
            },
          }));
        },
        onError: () => {
          handleErrorToast({
            toastMessage: 'Translation failed',
            toastDescription: 'Could not translate UI copy for this language.',
          });
        },
      },
    );
  }, [mode, activeEditingLocale, cachedLocaleUiCopy, translateFormUiCopy]);

  const [
    duplicateCategoryBannerDismissed,
    setDuplicateCategoryBannerDismissed,
  ] = useState(false);

  useEffect(() => {
    if (categoryName.trim()) {
      setDuplicateCategoryBannerDismissed(false);
    }
  }, [categoryName]);

  useEffect(() => {
    if (!isDuplicateManagementDraft) {
      setDuplicateCategoryBannerDismissed(false);
    }
  }, [isDuplicateManagementDraft]);

  const showDuplicateCategoryMandatoryBanner =
    isDuplicateManagementDraft &&
    !isEditMode &&
    !categoryName.trim() &&
    !duplicateCategoryBannerDismissed;

  // Compute effective title/description based on active locale
  const effectiveTitle = useMemo(() => {
    if (activeEditingLocale === DEFAULT_FORM_EDITING_LOCALE) {
      return title;
    }
    return titleTranslations[activeEditingLocale] ?? title;
  }, [activeEditingLocale, title, titleTranslations]);

  const effectiveDescription = useMemo(() => {
    if (activeEditingLocale === DEFAULT_FORM_EDITING_LOCALE) {
      return description;
    }
    return descriptionTranslations[activeEditingLocale] ?? description;
  }, [activeEditingLocale, description, descriptionTranslations]);

  // Allow manual edits to title/description translations
  const handleSetTitle = useCallback(
    (newTitle: string) => {
      if (activeEditingLocale === DEFAULT_FORM_EDITING_LOCALE) {
        setTitle(newTitle);
      } else {
        // Update translation for non-English locale
        setTitleTranslations((prev) => ({
          ...prev,
          [activeEditingLocale]: newTitle,
        }));
      }
    },
    [activeEditingLocale, setTitle, setTitleTranslations],
  );

  const handleSetDescription = useCallback(
    (newDescription: string) => {
      if (activeEditingLocale === DEFAULT_FORM_EDITING_LOCALE) {
        setDescription(newDescription);
      } else {
        // Update translation for non-English locale
        setDescriptionTranslations((prev) => ({
          ...prev,
          [activeEditingLocale]: newDescription,
        }));
      }
    },
    [activeEditingLocale, setDescription, setDescriptionTranslations],
  );

  return (
    <>
      {isFormTranslating && (
        <FormTranslatingPaneOverlay
          role="dialog"
          aria-modal="true"
          aria-busy="true"
          aria-labelledby="speak-up-translate-overlay-title"
          data-testid="speak-up-translate-loading-overlay"
        >
          <FormTranslationOverlayStack>
            <Spinner variant="secondary" size="xlarge" />
            <FormTranslationOverlayCopy id="speak-up-translate-overlay-title">
              Translating questions for this language…
            </FormTranslationOverlayCopy>
          </FormTranslationOverlayStack>
        </FormTranslatingPaneOverlay>
      )}
      <PageContent>
        <GridContainer>
          <GridMainColumn>
            {showDuplicateCategoryMandatoryBanner && (
              <CategoryWarningBannerWrapper>
                <InformationBanner
                  state={FormState.WARNING}
                  description="You have no category selected. Selecting a category is mandatory for this form."
                  $fullWidth
                  data-testid="duplicate-draft-category-warning"
                />
              </CategoryWarningBannerWrapper>
            )}
            <MainContent
              $isInitialState={mode === 'initial' || mode === 'loading'}
            >
              {mode === 'initial' ? (
                <AtlasMuiThemeProvider>
                  <CategorySelection
                    onCategorySelect={onCategorySelect}
                    isCoiForm={!isSpeakUpForm}
                    useAtlasStyle={true}
                    maxVisibleItems={4}
                  />
                </AtlasMuiThemeProvider>
              ) : mode === 'loading' ? (
                <LanguageVariants
                  mode={mode}
                  selectedLanguages={selectedLanguages}
                  onAddDefaultLanguage={onAddDefaultLanguage}
                />
              ) : (
                <>
                  <LanguageVariants
                    mode="content"
                    selectedLanguages={selectedLanguages}
                    onAddDefaultLanguage={onAddDefaultLanguage}
                    editingLocale={activeEditingLocale}
                    onEditingLocaleChange={setActiveEditingLocale}
                  />
                  <FormTitleAndDescription
                    key={activeEditingLocale}
                    mode={mode}
                    title={effectiveTitle}
                    setTitle={handleSetTitle}
                    description={effectiveDescription}
                    setDescription={handleSetDescription}
                    titleTouched={titleTouched}
                    setTitleTouched={setTitleTouched}
                    descriptionTouched={descriptionTouched}
                    setDescriptionTouched={setDescriptionTouched}
                  />
                  <Questions
                    mode={mode}
                    questions={questions}
                    setQuestions={setQuestions}
                    questionRefs={questionRefs}
                    editingLocale={activeEditingLocale}
                    questionCardCopy={
                      questionCardCopyByLocale[activeEditingLocale]
                    }
                  />
                </>
              )}
            </MainContent>
          </GridMainColumn>

          <ConfigSidebar>
            <Configuration
              selectedLanguages={selectedLanguages}
              setSelectedLanguages={setSelectedLanguages}
              categoryName={categoryName}
              setCategoryName={setCategoryName}
              setCategoryId={setCategoryId}
              categoryNameTouched={categoryNameTouched}
              setCategoryNameTouched={setCategoryNameTouched}
              categoryDescription={categoryDescription}
              setCategoryDescription={setCategoryDescription}
              categoryDescriptionTouched={categoryDescriptionTouched}
              setCategoryDescriptionTouched={setCategoryDescriptionTouched}
              selectedCountry={selectedCountry}
              setSelectedCountry={setSelectedCountry}
              countryTouched={countryTouched}
              setCountryTouched={setCountryTouched}
              autoReviewEnabled={autoReviewEnabled}
              setAutoReviewEnabled={setAutoReviewEnabled}
              onSpeakUpCategoryChosenInConfig={onSpeakUpCategoryChosenInConfig}
              isEditMode={isEditMode}
              isDuplicateManagementDraft={isDuplicateManagementDraft}
            />
          </ConfigSidebar>
        </GridContainer>
      </PageContent>
    </>
  );
};
